import DOMPurify from 'dompurify';

// Pre-compiled regexes — created once at module load, reused across all calls.
const RE_ENCODED_SCRIPT   = /&lt;script\b[^]*?&lt;\/script\s*&gt;/gi;
const RE_ENCODED_STYLE    = /&lt;style\b[^]*?&lt;\/style\s*&gt;/gi;
const RE_ENCODED_NOSCRIPT = /&lt;noscript\b[^]*?&lt;\/noscript\s*&gt;/gi;
const RE_LT               = /&lt;/g;
const RE_GT               = /&gt;/g;
const RE_AMP              = /&amp;/g;
const RE_QUOT             = /&quot;/g;
const RE_APOS             = /&#39;|&apos;/g;
const RE_CRLF             = /\r\n/g;
const RE_MD_BOLD_STAR     = /\*\*([^*\n]+)\*\*/g;
const RE_MD_BOLD_UNDER    = /__([^_\n]+)__/g;
const RE_MD_EM_STAR       = /\*([^*\n]+)\*/g;
const RE_MD_EM_UNDER      = /_([^_\n]+)_/g;
const RE_TAG_B            = /<\/?b\s*>/gi;
const RE_TAG_I            = /<\/?i\s*>/gi;
const RE_MD_CODE          = /`([^`\n]+)`/g;
const RE_NEWLINE          = /\n/g;

/**
 * Converts a subset of Markdown syntax to sanitized HTML.
 * Optionally appends a clickable link for the post URL.
 */
export function toSafeMarkdownHtml(text: string, postUrl?: string): string {
  // Only allow the class attribute on <span> elements, and only the
  // 'term-highlight' class — all other classes are stripped.
  const hook = (node: Element) => {
    if (node.tagName !== 'SPAN') {
      node.removeAttribute('class');
      return;
    }
    if (node.getAttribute('class') !== 'term-highlight') {
      node.removeAttribute('class');
    }
  };

  // Remove encoded <script>, <style>, and <noscript> blocks — including their
  // entire content — before entity decoding. This is an environment-independent
  // safeguard: it ensures JavaScript and CSS code never enters the DOM pipeline
  // even when DOMPurify's script-element removal behaves differently across
  // runtimes. Truncated blocks (no closing tag) are caught by DOMPurify below.
  const withoutCode = text
    .replaceAll(RE_ENCODED_SCRIPT, '')
    .replaceAll(RE_ENCODED_STYLE, '')
    .replaceAll(RE_ENCODED_NOSCRIPT, '');

  // Decode HTML character references (e.g. &lt;div&gt; → <div>) so that
  // structural tags arriving as encoded entities from scraped pages become
  // real tags that DOMPurify can strip in the next pass.
  const decoded = withoutCode
    .replaceAll(RE_LT, '<')
    .replaceAll(RE_GT, '>')
    .replaceAll(RE_AMP, '&')
    .replaceAll(RE_QUOT, '"')
    .replaceAll(RE_APOS, "'");

  // Strip all structural HTML tags from the decoded text, keeping only inline
  // formatting and span.term-highlight so subsequent markdown transforms still
  // work and search-highlight spans survive to the final output.
  const purifyStrip = DOMPurify(globalThis);
  purifyStrip.addHook('afterSanitizeAttributes', hook);
  const stripped = purifyStrip.sanitize(decoded, {
    ALLOWED_TAGS: ['span', 'b', 'i', 'u', 'strong', 'em', 'code', 'br'],
    ALLOWED_ATTR: ['class'],
  });
  purifyStrip.removeHook('afterSanitizeAttributes');

  const html = stripped
    .replaceAll(RE_CRLF, '\n')
    .replaceAll(RE_MD_BOLD_STAR, '<strong>$1</strong>')
    .replaceAll(RE_MD_BOLD_UNDER, '<strong>$1</strong>')
    .replaceAll(RE_MD_EM_STAR, '<em>$1</em>')
    .replaceAll(RE_MD_EM_UNDER, '<em>$1</em>')
    .replaceAll(RE_TAG_B, (tag) => (tag[1] === '/' ? '</strong>' : '<strong>'))
    .replaceAll(RE_TAG_I, (tag) => (tag[1] === '/' ? '</em>' : '<em>'))
    .replaceAll(RE_MD_CODE, '<code>$1</code>')
    .replaceAll(RE_NEWLINE, '<br />');

  // Always sanitize the text content without <a> so that any anchor tags
  // already present in the post text are stripped regardless of postUrl.
  const purify = DOMPurify(globalThis);
  purify.addHook('afterSanitizeAttributes', hook);
  const cleanText = purify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'code', 'br', 'u', 'span'],
    ALLOWED_ATTR: ['class'],
  });
  purify.removeHook('afterSanitizeAttributes');

  if (!postUrl) return cleanText;

  // Sanitize the appended link in a separate pass so only this one <a> is
  // allowed and the postUrl value is properly escaped by DOMPurify.
  const rawLink = `<a href="${postUrl}" class="classification-card__post-link" target="_blank" rel="noopener noreferrer">${postUrl}</a>`;
  const cleanLink = DOMPurify(globalThis).sanitize(rawLink, {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });

  return `${cleanText} ${cleanLink}`;
}
